import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  IndustryDetailsImageUpload,
  createIndustry,
  getIndustryByID,
  updateIndustry,
} from "./services/industry.services";

import { clearIndustryDetails } from "./industrySlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateIndustry: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const IndustryID = location.state?.IndustryID;
  const IndustryDetail = useAppSelector(
    (state) => state.industry.IndustryDetail,
  );
  const loading = useAppSelector((state) => state.industry.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (IndustryID) {
      dispatch(getIndustryByID(IndustryID));
    }
    return () => {
      dispatch(clearIndustryDetails());
    };
  }, [IndustryID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [IndustryDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values?.Title ? values?.Title : "",
      URL: values?.URL ? values?.URL : "",
      ShortDescription: values?.ShortDescription
        ? values?.ShortDescription
        : "",
      LongDescription: values?.LongDescription ? values?.LongDescription : "",
      IndustryHeading: values?.IndustryHeading ? values?.IndustryHeading : "",
      MetaTitle: values?.MetaTitle ? values?.MetaTitle : "",
      MetaKeyword: values?.MetaKeyword ? values?.MetaKeyword : "",
      MetaDescription: values?.MetaDescription ? values?.MetaDescription : "",
      IndustrySuppliers: values?.IndustrySuppliers
        ? values?.IndustrySuppliers
        : "",
      AboutIndustry: values?.AboutIndustry ? values?.AboutIndustry : "",
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? values.MainImage[0].getRawFile()
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";
    const AboutIndustryImage =
      values?.AboutIndustryImage &&
      typeof values?.AboutIndustryImage !== "string"
        ? values.AboutIndustryImage[0].getRawFile()
        : "";
    try {
      if (IndustryID) {
        const editPayload = {
          ...formData,
          ID: IndustryID,
        };

        const response = await dispatch(updateIndustry(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            (values?.AboutIndustryImage &&
              typeof values?.AboutIndustryImage !== "string") ||
            values?.AboutIndustryAlterTag
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("AboutIndustryImage", AboutIndustryImage);
            ImageData.append(
              "AboutIndustryAlterTag",
              values?.AboutIndustryAlterTag || "",
            );
            ImageData.append("ID", IndustryID);
            await dispatch(IndustryDetailsImageUpload(ImageData));
          }
          navigate("/industry");
        }
      } else {
        const response = await dispatch(createIndustry(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.MainImage && typeof values?.MainImage !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string") ||
              (values?.AboutIndustryImage &&
                typeof values?.AboutIndustryImage !== "string") ||
              values?.AboutIndustryAlterTag) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("AboutIndustryImage", AboutIndustryImage);
            ImageData.append(
              "AboutIndustryAlterTag",
              values?.AboutIndustryAlterTag || "",
            );
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(IndustryDetailsImageUpload(ImageData));
          }
          navigate("/industry");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={IndustryDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {IndustryID ? "Update Industry" : "Create Industry"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e: any) => {
                        formRenderProps.onChange("URL", {
                          value: e?.value
                            ? e?.value
                                ?.toLowerCase()
                                ?.trim(" ")
                                ?.replace(/\s+/g, "-")
                            : "",
                        });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{ display: "flex", gap: 10 }}
                  >
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="IndustryHeading"
                      label="Heading"
                      placeholder="Heaiding"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="IndustrySuppliers"
                      label="Industry Suppliers"
                      placeholder="Industry Suppliers"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    rowSpan={3}
                    className="d-flex gap-2"
                    colSpan={3}
                  >
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                    {/* </GridLayoutItem>
                  <GridLayoutItem rowSpan={3} colSpan={2}> */}
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="LongDescription"
                      label="Long Description"
                      placeholder="Long Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2} rowSpan={2}>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="AboutIndustry"
                      label="About Industry"
                      placeholder="About Industry"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`AboutIndustryImage`}
                      label="About Industry Image"
                      type="file"
                      accept="image/*"
                      alternateName="About Industry Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="AboutIndustryAlterTag"
                      label="Image Alt"
                      placeholder="Image Alt"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaKeyword"
                      label="Meta Keyword"
                      placeholder="Meta Keyword"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Main Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%", paddingTop: "10px" }}>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={IndustryID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/industry")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateIndustry;
